// Abgeleitete Werte der Dienstleister-Detailseite (Handoff Dienstleister §9). Reine Funktionen – testbar.

import { deDatum } from '../immobilien/objektDetail';
import { aktuelleNachweise, NACHWEIS_LABEL, nachweisStatus, type NachweisArt, type NachweisStatus } from './nachweise';
import { istVertrag, zuordnungLaeuft, type DlObjektRoh, type DlZeile } from './liste';

// ── Ticket-Stufen (Entscheidung 14.10.2026) ────────────────────────────────────

export type TicketStufe = 'neu' | 'beauftragt' | 'in_arbeit' | 'erledigt' | 'abgerechnet';
export const TICKET_STUFEN: readonly { key: TicketStufe; label: string; ton: 'info' | 'acc' | 'warn' | 'ok' | 'neutral' }[] = [
  { key: 'neu', label: 'Neu', ton: 'info' }, { key: 'beauftragt', label: 'Beauftragt', ton: 'acc' }, { key: 'in_arbeit', label: 'In Arbeit', ton: 'warn' },
  { key: 'erledigt', label: 'Erledigt', ton: 'ok' }, { key: 'abgerechnet', label: 'Abgerechnet', ton: 'neutral' },
];

/** Ticket-Status → Stufe: Neu = neu/offen, Beauftragt = warten_auf_dienstleister, In Arbeit = in_bearbeitung (+ Rückmeldung/eskaliert), Erledigt, Abgerechnet = geschlossen. */
export function ticketStufe(status: string | null): TicketStufe {
  switch ((status ?? '').toLowerCase()) {
    case 'warten_auf_dienstleister': return 'beauftragt';
    case 'in_bearbeitung': case 'warten_auf_rueckmeldung': case 'eskaliert': return 'in_arbeit';
    case 'erledigt': return 'erledigt';
    case 'geschlossen': return 'abgerechnet';
    default: return 'neu';
  }
}
export const stufeLabel = (s: TicketStufe) => TICKET_STUFEN.find(x => x.key === s)?.label ?? s;
export const istOffeneStufe = (s: TicketStufe) => s === 'neu' || s === 'beauftragt' || s === 'in_arbeit';

// ── Kopf ───────────────────────────────────────────────────────────────────────

/** „HANDWERK · NOTFALL 24/7 · BEVORZUGT · Partner seit …“ (Großschreibung übernimmt das CSS). */
export function kicker(z: Pick<DlZeile, 'handwerk' | 'notfall' | 'bevorzugt'>, vertretung: boolean, partnerSeit: string | null): string {
  return [
    z.handwerk ? 'Handwerk' : 'Dienstleistung', z.notfall ? 'Notfall 24/7' : '', z.bevorzugt ? (vertretung ? 'Vertretung' : 'Bevorzugt') : '',
    partnerSeit ? `Partner seit ${deDatum(partnerSeit.slice(0, 10))}` : '',
  ].filter(Boolean).join(' · ');
}

// ── Verträge ───────────────────────────────────────────────────────────────────

export interface VertragsInfo { anzahl: number; kostenJahr: number; naechstesEnde: string | null }

export function vertragsInfo(zuordnungen: readonly Pick<DlObjektRoh, 'vertragsart' | 'aktiv' | 'bis' | 'kuendigung_zum' | 'kosten_jahr'>[], heute: string): VertragsInfo {
  const laufend = zuordnungen.filter(o => zuordnungLaeuft(o, heute) && istVertrag(o.vertragsart));
  const enden = laufend.flatMap(o => [o.bis, o.kuendigung_zum].filter((x): x is string => !!x).map(x => x.slice(0, 10)).sort().slice(0, 1)).filter(d => d >= heute).sort();
  return {
    anzahl: laufend.length,
    kostenJahr: laufend.reduce((s, o) => s + (Number(o.kosten_jahr ?? 0) || 0), 0),
    naechstesEnde: enden[0] ?? null,
  };
}

// ── Nachweise ──────────────────────────────────────────────────────────────────

export interface NachweisZeile { art: NachweisArt; label: string; gueltigBis: string | null; status: NachweisStatus; vorhanden: boolean }

/** Je Art der aktuelle Nachweis; § 48b nur bei Handwerk erwartet, fehlende werden als „nicht hinterlegt“ geführt. */
export function nachweisZeilen(rows: readonly { art: string; gueltig_bis: string | null }[], handwerk: boolean, heute: string): NachweisZeile[] {
  const aktuell = new Map(aktuelleNachweise(rows.map(r => ({ ...r, dienstleister_id: '' }))).map(r => [r.art, r]));
  const arten: NachweisArt[] = handwerk ? ['freistellung_48b', 'haftpflicht', 'gewerbe', 'mindestlohn'] : ['haftpflicht', 'gewerbe', 'mindestlohn', 'freistellung_48b'];
  return arten
    .filter(a => aktuell.has(a) || a !== 'freistellung_48b' || handwerk)
    .map(a => {
      const r = aktuell.get(a);
      return { art: a, label: NACHWEIS_LABEL[a], gueltigBis: r?.gueltig_bis ?? null, status: r ? nachweisStatus(r.gueltig_bis, heute) : 'gueltig', vorhanden: !!r };
    });
}

/** § 48b: Handwerk und Freistellung abgelaufen oder fehlend → 15 % Bauabzug einbehalten. */
export function bauabzugNoetig(handwerk: boolean, nachweise: readonly NachweisZeile[]): boolean {
  if (!handwerk) return false;
  const f = nachweise.find(n => n.art === 'freistellung_48b');
  return !f || !f.vorhanden || f.status === 'abgelaufen';
}

// ── Bewertung ──────────────────────────────────────────────────────────────────

export const sterne = (wert: number) => '★★★★★☆☆☆☆☆'.slice(5 - Math.round(Math.max(0, Math.min(5, wert))), 10 - Math.round(Math.max(0, Math.min(5, wert))));
export const komma1 = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// ── KI-Zusammenfassung (aus Fakten, bis eine KI-Formulierung angebunden ist) ──

export function kiText(p: {
  name: string; ansprechpartner: string; partnerSeit: string | null; heute: string; kategorien: readonly string[];
  objekte: readonly string[]; bewertung: number | null; offeneAuftraege: number; offen: number;
  nachweise: readonly NachweisZeile[];
}): string {
  const teile: string[] = [];
  const ap = p.ansprechpartner && !/zentrale|service|kundenservice/i.test(p.ansprechpartner) ? ` (Ansprechpartner ${p.ansprechpartner})` : '';
  const dauer = (() => {
    if (!p.partnerSeit) return '';
    const m = (Number(p.heute.slice(0, 4)) - Number(p.partnerSeit.slice(0, 4))) * 12 + Number(p.heute.slice(5, 7)) - Number(p.partnerSeit.slice(5, 7));
    if (m < 1) return ' seit diesem Monat';
    return m >= 12 ? ` seit ${Math.floor(m / 12)} ${Math.floor(m / 12) === 1 ? 'Jahr' : 'Jahren'}` : ` seit ${m} ${m === 1 ? 'Monat' : 'Monaten'}`;
  })();
  teile.push(`${p.name}${ap} arbeitet${dauer} für uns${p.kategorien.length ? `, Gewerke: ${p.kategorien.join(', ')}` : ''}.`);
  teile.push(p.objekte.length ? `Zugeordnet zu ${p.objekte.slice(0, 3).join(', ')}${p.objekte.length > 3 ? ` und ${p.objekte.length - 3} weiteren Objekten` : ''}.` : 'Keine feste Objekt-Zuordnung, Beauftragung im Einzelfall.');
  if (p.bewertung !== null) teile.push(`Bewertung ${komma1(p.bewertung)} von 5.`);
  teile.push(p.offeneAuftraege ? `${p.offeneAuftraege} ${p.offeneAuftraege === 1 ? 'Auftrag ist' : 'Aufträge sind'} offen.` : 'Keine offenen Aufträge.');
  if (p.offen > 0.005) teile.push(`Offene Rechnungen: ${p.offen.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}.`);
  const ab = p.nachweise.filter(n => n.vorhanden && n.status === 'abgelaufen').map(n => n.label);
  const bald = p.nachweise.filter(n => n.vorhanden && n.status === 'laeuft_ab').map(n => n.label);
  if (ab.length) teile.push(`Achtung: ${ab.join(', ')} abgelaufen.`);
  else if (bald.length) teile.push(`${bald.join(', ')} läuft bald ab.`);
  else if (p.nachweise.some(n => n.vorhanden)) teile.push('Alle Nachweise sind gültig.');
  return teile.join(' ');
}

export const ibanKurz = (iban: unknown): string | null => {
  const s = typeof iban === 'string' ? iban.replace(/\s/g, '') : '';
  return s.length >= 4 ? `•••• ${s.slice(-4)}` : null;
};
